import classNames from "classnames";
import React, { useState } from "react";
import { Button, Menu, Wrapper } from "react-aria-menubutton";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import ImportOfflineHybridFileDialog from "./ImportOfflineHybridFileDialog";
import style from "./mcs.scss";
import { McsFilter, McsQuickFilters } from "./McsQuickFilters";
import McsTable from "./McsTable";
import CopyToClipboard from "components/copy-to-clipboard/CopyToClipboard";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import { LicenseExpire } from "components/icons/LicenseExpire";
import { Licenses } from "components/icons/Licenses";
import { NoAccess } from "components/icons/NoAccess";
import { SuccessIcon } from "components/icons/SuccessIcon";
import ApplicationLayout from "components/layout/ApplicationLayout";
import layoutStyle from "components/layout/layout.scss";
import { MANAGEMENT_CONSOLE_LICENSE } from "components/licenses/bundles";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import MenuItemButton from "components/menu-item-button/MenuItemButton";
import Modal from "components/modal/Modal";
import { VideoTutorialButton } from "components/modal/video-modal/video-tutorial-button/VideoTutorialButton";
import { VideoModal } from "components/modal/video-modal/VideoModal";
import { ALL_MCS_ROUTE } from "components/router/Routes";
import Tooltip from "components/tooltip/Tooltip";
import { AUTH_MC_CREATE, AUTH_MC_VIEW } from "domain/authority";
import { ContainerData, EmsConfiguration, LicenseData, MIN_DATE } from "domain/licenses";
import { McTable } from "domain/mc";
import { TutorialVideoLink } from "domain/tutorialVideos";
import { hybridMcService } from "services/hybrid-mc/HybridMcService";
import { licenseService, ProductRateList } from "services/licenses/LicenseService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import {
    getCurrentTenantDetails,
    hasTenantCookie,
    isInTenantAccess,
    isLicensingModelBundleWithToken,
} from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import buttonsStyle from "styles/buttons.scss";
import { isExpired } from "utils/format";
import { Logger } from "utils/logging";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

export type MenuAction = "REGISTER" | "SYNC";

const LOGGER = new Logger("AllMcsView");

interface LocationState {
    autoOpenCreateEntitlement?: boolean;
}

export default function AllMcsView(): JSX.Element {
    const { t } = useTranslation();
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const theme = useTheme();
    const location = useLocation<LocationState>();
    const [registrationToken, setRegistrationToken] = useState<string>("");
    const [activeFilter, setActiveFilter] = useState<McsFilter>(McsFilter.ACTIVE);
    const [uploadFileModalOpen, setUploadFileModalOpen] = useState(false);
    const [offlineAction, setOfflineAction] = useState<MenuAction>("REGISTER");
    const [state, setState] = useState({ count: 0 });
    const [refreshCount, setRefreshCount] = React.useState(0);
    const [mcFetchData, setMcFetchData] = React.useState<McTable>();
    const [onPremiseLicense, setOnPremiseLicense] = React.useState<{ availableLicenses: number; expiration: string }>({
        availableLicenses: 0,
        expiration: MIN_DATE.toISOString(),
    });
    const [showVideoModal, setShowVideoModal] = React.useState(false);
    const watchTutorialButton = <VideoTutorialButton onClick={() => setShowVideoModal(true)} />;
    const LOCALHOST_URL = "https://localhost:8443/onprem/#registeroffline";

    function onRefreshing() {
        setRefreshCount((prevState) => prevState + 1);
    }
    const handleDataFetch = (data: McTable) => {
        setMcFetchData(data);
    };

    const uploadOfflineRegistrationFileTitle = t("Mcs.offline.registerAndSyncButton");
    const [modalTitle, setModalTitle] = useState<string>(uploadOfflineRegistrationFileTitle);
    const emptyConfiguration: EmsConfiguration = {
        emsId: "",
        hlEntitlements: false,
        slEntitlements: false,
        availableSlActivations: 0,
        usedSlActivations: 0,
        lastSynced: null,
    };
    const fetchAvailableData = async () => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        const tenantUuid = getCurrentTenantDetails().uuid;

        const [licenseResult, emsResult, tokenRateResult] = await Promise.allSettled([
            licenseService.fetchAllLicenses({ abortController, own: !hasTenantCookie() }),
            licenseService.fetchEmsConfiguration(tenantUuid),
            isLicensingModelBundleWithToken()
                ? licenseService.fetchProductTokenRate(abortController, tenantUuid)
                : Promise.resolve(null),
        ]);

        const licenses = licenseResult.status === "fulfilled" ? licenseResult.value.licenses.licenseData : [];
        const emsConfiguration = emsResult.status === "fulfilled" ? emsResult.value : emptyConfiguration;
        const tokenRates = tokenRateResult.status === "fulfilled" ? tokenRateResult.value : null;

        let allContainers: ContainerData["containers"]["containers"] = [];
        if (emsConfiguration.slEntitlements) {
            try {
                allContainers = await licenseService.fetchAllContainersRecursively(abortController, null, 0);
            } catch (err) {
                LOGGER.error("Exception occurred while fetching containers: ", err);
            }
        }

        const licenseTokenRateList: ProductRateList[] = [];
        if (tokenRates) {
            tokenRates.forEach((each) => {
                each.productToRate.forEach((product) => licenseTokenRateList.push(product));
            });
        }

        return {
            licenses,
            emsConfiguration,
            allContainers,
            tokenRates: licenseTokenRateList,
        };
    };

    const findAvailableMcLicenseData = (licenses: LicenseData[], licenseType: string) => {
        const availableMcLicense = licenses.find((each) => each.type === licenseType);

        return {
            availableLicenses: availableMcLicense ? availableMcLicense.available : 0,
            expiration: availableMcLicense ? availableMcLicense.expirationDate : MIN_DATE.toISOString(),
        };
    };

    const fetchSyncIntervalData = async (tenantUuid: string) => {
        try {
            const syncIntervalData = await hybridMcService.fetchIntervals(tenantUuid);
            return {
                offlineSyncEnabled: syncIntervalData.offlineSync,
            };
        } catch (err) {
            LOGGER.error("Exception occurred while fetching sync interval data: ", err);
            return { offlineSyncEnabled: false };
        }
    };

    const lastValidLicenseData = React.useRef({
        availableLicenses: 0,
        expiration: MIN_DATE.toISOString(),
    });

    const { data, isLoading: isLoadingLicenceData } = useQuery(
        ["licenses", "emsConfiguration", "allContainers", "tokenRates"],
        () => fetchAvailableData()
    );

    React.useEffect(() => {
        if (data) {
            const foundLicense = findAvailableMcLicenseData(data.licenses, MANAGEMENT_CONSOLE_LICENSE);
            setOnPremiseLicense(foundLicense);
            lastValidLicenseData.current.availableLicenses = foundLicense.availableLicenses;
        }
    }, [data]);

    const { data: { offlineSyncEnabled } = { offlineSyncEnabled: false }, isLoading: isLoadingOfflineSyncData } =
        useQuery(["offlineSyncEnabled"], () => fetchSyncIntervalData(getCurrentTenantDetails().uuid));

    function updateTableView() {
        return setState((prevState) => ({ count: prevState.count + 1 }));
    }

    const toggleSetUploadFileModalOpen = () => {
        setUploadFileModalOpen((prev) => !prev);
    };

    const handleCopyToken = () => {
        hybridMcService
            .createMcRegistrationToken()
            .then((registrationToken) => {
                setRegistrationToken(registrationToken.token);
                return navigator.clipboard.writeText(registrationToken.token).then(() => {
                    toast(
                        <SuccessIcon
                            successClass={layoutStyle.customToastSuccessIcon}
                            color={theme.contentBackgroundColor}
                            text={t("Mcs.copyRegistrationCodeSuccess")}
                        />,
                        {
                            closeButton: (closeToastProps) => (
                                <CustomToastCloseButton
                                    closeToast={{ ...closeToastProps }}
                                    color={theme.iconFillColor}
                                />
                            ),
                            className: layoutStyle.customToastSuccessMessage,
                        }
                    );
                });
            })
            .catch((error) => LOGGER.error("Couldn't copy to clipboard", error));
    };

    const handleSelection = (value: MenuAction) => {
        switch (value) {
            case "REGISTER":
                setOfflineAction(value);
                setModalTitle(t("Mcs.offline.registerAndSyncButton"));
                setUploadFileModalOpen(true);
                break;
            case "SYNC":
                setOfflineAction(value);
                setModalTitle(t("Mcs.offline.syncButton"));
                setUploadFileModalOpen(true);
                break;
            default:
                LOGGER.error("Unknown menu action", value);
                break;
        }
    };
    const availableLicenses = onPremiseLicense.availableLicenses ?? lastValidLicenseData.current.availableLicenses;
    const expiration = onPremiseLicense.expiration ?? lastValidLicenseData.current.expiration;
    const userHasAuthority = userSessionService.userHasAnyAuthority([AUTH_MC_CREATE, AUTH_MC_VIEW]);
    const licensesAvailable = (availableLicenses ?? 0) > 0;
    const licenseExpired = expiration == null || isExpired(expiration);
    const hasMcData = (mcFetchData?.mcdata?.length ?? 0) > 0;
    const licenseDeliveryCheck =
        availableLicenses == 0 && (expiration == undefined || expiration == MIN_DATE.toISOString()) && !hasMcData;
    const hybridViewActions = (
        <>
            {userHasAuthority ? (
                <>
                    {!licenseExpired ? (
                        <div className={style.licenseExpire}>
                            <Tooltip content={t("Mcs.availableLicensesInfo")}>
                                <div className={style.availableLicensesContainer}>
                                    <Licenses size={20} color={theme.textColor} />
                                    <div data-testid={testIds.workArea.mcs.availableLicensesLabel}>
                                        {availableLicenses}
                                    </div>
                                </div>
                            </Tooltip>
                            {offlineSyncEnabled && (
                                <>
                                    <Wrapper
                                        className={classNames(style.kebabIconContainer)}
                                        onSelection={(value: MenuAction) => {
                                            handleSelection(value);
                                        }}
                                    >
                                        <Button>
                                            <button
                                                className={buttonsStyle.secondaryButtonWithoutIcon}
                                                data-testid={testIds.workArea.mcs.offlineMenu.button}
                                            >
                                                {t("Mcs.offline.offlineSyncButton")}
                                            </button>
                                        </Button>
                                        <Menu
                                            className={classNames(style.kebabMenu)}
                                            data-testid={testIds.workArea.mcs.offlineMenu.itself}
                                        >
                                            <ul>
                                                <li
                                                    className={classNames(
                                                        style.listItem,
                                                        style.listTextItem,
                                                        style.noHover
                                                    )}
                                                >
                                                    <div className={style.menu}>
                                                        <div className={style.menuTitle}>
                                                            {t("Mcs.offline.offlineSyncMenuTitle")}
                                                        </div>
                                                        <div className={style.menuSubtitle}>
                                                            {t("Mcs.offline.offlineSyncMenuSubtitleUrl")}
                                                            <span className={style.localhostUrl}>
                                                                {LOCALHOST_URL}
                                                                <span className={style.copyToClipboard}>
                                                                    <CopyToClipboard value={LOCALHOST_URL} />
                                                                </span>
                                                            </span>
                                                        </div>
                                                        <div className={style.menuSubtitle}>
                                                            {t("Mcs.offline.offlineSyncMenuSubtitleReplace")}
                                                        </div>
                                                    </div>
                                                </li>
                                                <li className={style.listItem}>
                                                    <MenuItemButton
                                                        value={"REGISTER"}
                                                        className={style.menuItem}
                                                        data-testid={
                                                            testIds.workArea.mcs.offlineMenu.registerAndSyncButton
                                                        }
                                                        disabled={!licensesAvailable}
                                                    >
                                                        {uploadOfflineRegistrationFileTitle}
                                                    </MenuItemButton>
                                                </li>
                                                <li className={style.listItem}>
                                                    <MenuItemButton
                                                        value={"SYNC"}
                                                        className={style.menuItem}
                                                        data-testid={testIds.workArea.mcs.offlineMenu.syncButton}
                                                    >
                                                        {t("Mcs.offline.syncButton")}
                                                    </MenuItemButton>
                                                </li>
                                            </ul>
                                        </Menu>
                                    </Wrapper>

                                    <Modal
                                        isOpen={uploadFileModalOpen}
                                        hideModal={toggleSetUploadFileModalOpen}
                                        modalTitle={modalTitle}
                                    >
                                        <ImportOfflineHybridFileDialog
                                            onClose={toggleSetUploadFileModalOpen}
                                            setModalTitle={setModalTitle}
                                            action={offlineAction}
                                        />
                                    </Modal>
                                </>
                            )}
                            <button
                                className={buttonsStyle.primaryButtonWithoutIcon}
                                onClick={() => {
                                    handleCopyToken();
                                    usageStatisticsService.sendEvent({
                                        category: Category.MCS,
                                        action: Action.REGISTER_MC,
                                    });
                                }}
                                data-testid={testIds.workArea.mcs.getRegistrationCodeButton}
                                data-token={JSON.stringify({
                                    token: registrationToken,
                                })}
                                disabled={!licensesAvailable}
                            >
                                {t("Mcs.copyRegistrationCode")}
                            </button>
                        </div>
                    ) : (
                        <div className={style.availableLicensesContainer}>
                            <LicenseExpire size={20} color={theme.textColor} />
                            <div data-testid={testIds.workArea.mcs.licenseExpiredLabel}>{t("Mcs.licenseExpired")}</div>
                        </div>
                    )}
                </>
            ) : (
                <div className={style.missingAuthorityWrapper}>
                    <NoAccess color={theme.textColor} />
                    <span>{t("Mcs.authorityMissing")}</span>
                </div>
            )}
        </>
    );

    const viewActions = (
        <>
            {watchTutorialButton}
            {isLoadingLicenceData || isLoadingOfflineSyncData ? (
                <LoadingIndicator small={true} />
            ) : (
                !isInTenantAccess() && <div className={style.viewActionsContainer}>{hybridViewActions}</div>
            )}
        </>
    );

    return (
        <>
            <ApplicationLayout
                viewTitle={t("Mcs.table.title")}
                enableRefresh={true}
                onHandleRefresh={onRefreshing}
                view={
                    <>
                        <McsQuickFilters activeFilter={activeFilter} onFilterChange={setActiveFilter} />
                        <McsTable
                            count={state.count}
                            licenseDeliveryCheck={licenseDeliveryCheck}
                            initialMcsData={{
                                tenantUuid: "",
                                name: "",
                                description: "",
                            }}
                            onMcChange={updateTableView}
                            licenses={data ? data.licenses : []}
                            emsConfiguration={data ? data.emsConfiguration : emptyConfiguration}
                            allContainers={data ? (data.allContainers ?? []) : []}
                            tokenRates={data && data.tokenRates}
                            refreshCount={refreshCount}
                            autoOpenCreateEntitlement={location.state?.autoOpenCreateEntitlement}
                            activeFilter={activeFilter}
                            onDataFetch={handleDataFetch}
                            isLoadingEmsData={isLoadingLicenceData}
                        />
                    </>
                }
                viewActions={viewActions}
                hideActions={watchTutorialButton}
                mainRoute={ALL_MCS_ROUTE}
            />
            <VideoModal
                isOpen={showVideoModal}
                hideModal={() => setShowVideoModal(false)}
                linkUrl={TutorialVideoLink.BMPOP}
            />
        </>
    );
}
